import React, { useEffect, useContext, useState }  from 'react'
import { View, StyleSheet, ScrollView, TouchableOpacity } from 'react-native'
import { Icon, Button, Text, Spinner, Modal, TabBar, Tab, useTheme } from '@ui-kitten/components'
import StyledTextInput from '../../components/StyledTextInput'
import { UserContext } from '../../global/UserProvider'
import { saveNewComplexSale, deleteComplexSale, deleteComplexSaleTemplate, updateComplexSaleTemplate, saveNewComplexSaleTemplate, saveUpdatedItemsForComplexSale, saveUpdatedFinalPriceForComplexSale, addComplexSalePayment, deleteComplexSalePayment, updateComplexSaleStatus, addComplexSaleComment, deleteComplexSaleComment } from '../../global/Firebase/firebase'
import PlatformDependantSelect from '../../components/PlatformDependantSelect'
import { snapshotExists } from '../../global/Firebase/types'
import * as Sentry from "@sentry/react-native"
import { sendFBAnalyticsEvent } from '../../global/Firebase/firebaseAnalytics'
import { usePostHog } from 'posthog-react-native'
import { useTranslation } from 'react-i18next'
import { productsDictAtom, currencyFormatterAtom, storesDictAtom, userRoleAtom, constantsAtom, usersDictAtom, suppliersDictAtom, productUnitsDictAtom, localeDateServiceAtom } from '../../global/AtomsProvider'
import { useAtomValue } from 'jotai'
import ClientAutoComplete from '../../components/ClientAutoComplete'
import ComplexSaleTemplatesList from './ComplexSaleTemplatesList'
import ProductSearch from '../../components/ProductSearch'
import Divider from '../../components/Divider'
import { CreateClientView } from '../../components/CreateClientView'
import { useColors } from '../../utils/useColors'
import { ClientView } from '../../components/ClientView'
import { FontAwesome5 } from '@expo/vector-icons'
import { PaymentMethodsSelect } from '../../components/PaymentMethodsSelect'
import { format, parse } from 'date-fns'
import AdaptiveTableCard from '../../components/AdaptiveTableCard'
import UniversalDetailScreen from '../../components/UniversalDetailScreen'
import { getDailyStatID } from '../../utils/timezoneFunctions'
import { useToast } from "react-native-toast-notifications"
// ATL-1143: the status table now has ONE definition (shared with the list screen and mirrored by
// the backend), and the SEED and the WRITE go through the same function on purpose — on this
// screen they are the same value. `status` state is written straight back to Firestore, so
// whatever seeds it is what a save persists.
//
// `statusForWrite` rather than `resolveStatusOption` (which the ATL-1103 sibling screens use with
// `?? STATUSES[0]`): for a bare id the table does not list — the legacy `active`, or anything a
// future release adds — `resolveStatusOption` returns null and the `??` silently seeds `new`, so
// merely opening a sale and saving would rewrite its status. `statusForWrite` keeps it as `{id}`:
// still a map, so `status.isActive` is never `undefined`, and still carrying no text nobody chose.
import { complexSaleStatuses, complexSaleIsActive, statusForWrite } from '../../utils/complexSaleStatus'
import { isWriteOutcomeUnknown, saveFailureText } from '../../global/Firebase/writeOutcome'

export default function ComplexSaleDetailScreen( { navigation, route } ) {
  const colors = useColors()
  const theme = useTheme()
  const { t } = useTranslation()
  const localizedStatuses = complexSaleStatuses(t)
  const { detail: passedDetail, preselectedTemplate, editTemplateMode, itemIndex, sectionIndex, addItemToData, editItemInData, removeItemFromData, preselectedStore, id } = route.params
  const [detail, setDetail] = useState(passedDetail)
  const [context, setContext] = useContext(UserContext)
  const db = context.db
  const productUnitsDict = useAtomValue(productUnitsDictAtom)
  const currencyFormatter = useAtomValue(currencyFormatterAtom)
  const storesDict = useAtomValue(storesDictAtom)
  const userRole = useAtomValue(userRoleAtom)
  const constants = useAtomValue(constantsAtom)
  const usersDict = useAtomValue(usersDictAtom)
  const postHog = usePostHog()
  const [store, setStore] = useState((detail && detail.store) ? detail.store : (preselectedStore || context.user.store?.id))
  const [template, setTemplate] = useState(detail ? detail.template : (preselectedTemplate != null ? preselectedTemplate : null))
  const [name, setName] = useState(detail && detail.name ? detail.name : null)
  const [templateName, setTemplateName] = useState(detail ? detail.templateName : null)
  const [createdDate, setCreatedDate] = useState((detail && detail.createdDate) ? (detail.createdDate?.toDate?.() ? detail.createdDate?.toDate?.() : detail.createdDate) : new Date())
  const [items, setItems] = useState(detail && detail.items ? detail.items : [])
  const [client, setClient] = useState(detail ? detail.client : null)
  const [filteredItems, setFilteredItems] = useState(items)
  const [createClientModalVisible, setCreateClientModalVisible] = useState(false)
  const [comments, setComments] = useState(detail ? detail.comments : [])
  const [updates, setUpdates] = useState(detail && detail.updates ? detail.updates : [])
  const [createCommentModalVisible, setCreateCommentModalVisible] = useState(false)
  const [status, setStatus] = useState(() => statusForWrite(detail?.status, localizedStatuses) ?? localizedStatuses[0])
  const [editMode, setEditMode] = useState(editTemplateMode ? true : (detail ? false : true))
  const [finalPrice, setFinalPrice] = useState(detail ? detail.finalPrice?.toString?.() : 0)
  const [payments, setPayments] = useState(detail?.payments || [])
  const [createPaymentModalVisible, setCreatePaymentModalVisible] = useState(false)
  const [paymentAmount, setPaymentAmount] = useState('')
  const [paymentDescription, setPaymentDescription] = useState('')
  const [paymentMethodForPayment, setPaymentMethodForPayment] = useState(null)
  const [editFinalPriceMode, setEditFinalPriceMode] = useState(false)
  const [commentText, setCommentText] = useState('')
  const [saveError, setSaveError] = useState('')
  const [successMessageVisible, setSuccessMessageVisible] = useState(false)
  const [isLoading, setIsLoading] = useState(false)
  const toast = useToast()


  useEffect(() => {
    navigation.setOptions({
      headerTitle: editTemplateMode ? (detail ? t('complexSales.editTemplate') : t('complexSales.newTemplate')) : (detail ? t('complexSales.editComplexSale') : t('complexSales.newComplexSale')),
    })
  }, [detail])

  useEffect(() => {
    const fetchComplexSaleById = async () => {
      if (id && !detail) {
        setIsLoading(true)
        try {
          const saleRef = db.collection('complexSales').doc(id)
          const doc = await saleRef.get()
          if (snapshotExists(doc)) {
            let fetchedDetail = doc.data()
            fetchedDetail.ref = saleRef
            fetchedDetail.id = saleRef.id
            
            // Update state with fetched data
            // It's important to set the detail object first for other useEffects or conditional renders
            setDetail(fetchedDetail)
            setTemplate(fetchedDetail.template)
            setName(fetchedDetail.name || null)
            setTemplateName(fetchedDetail.templateName || null)
            setCreatedDate(fetchedDetail.createdDate?.toDate?.() ? fetchedDetail.createdDate?.toDate?.() : fetchedDetail.createdDate || new Date())
            setItems(fetchedDetail.items || [])
            setFilteredItems(fetchedDetail.items || [])
            setClient(fetchedDetail.client || null)
            setComments(fetchedDetail.comments || [])
            setUpdates(fetchedDetail.updates || [])
            setStatus(statusForWrite(fetchedDetail.status, localizedStatuses) ?? localizedStatuses[0])
            setFinalPrice(fetchedDetail.finalPrice?.toString?.() || '0')
            setPayments(fetchedDetail.payments || [])
            setStore(fetchedDetail.store || context.user.store?.id)
            setEditMode(false) // Assuming fetched sales are not in edit mode by default
          } else {
            console.log("No such complex sale document!");
            setSaveError(t('complexSales.notFound'))
          }
        } catch (error) {
          console.error("Error fetching complex sale document:", error);
          Sentry.captureException(error)
          setSaveError(t('error'))
        } finally {
          setIsLoading(false)
        }
      }
    }

    fetchComplexSaleById()
  }, [id])

  const handleDelete = async () => {
    if (!isLoading) {
      try {
        setIsLoading(true)
        if (editTemplateMode) {
          await deleteComplexSaleTemplate(detail.ref)
        } else {
          await deleteComplexSale(db, detail, context.user.uid)
          removeItemFromData(sectionIndex, itemIndex)
        }
        setIsLoading(false)
        navigation.goBack()
      } catch(error) {
        console.log(error)
        setSaveError(t('error'))
        Sentry.captureException(error, { tags: { books_write_error: 'complex-sale-delete' } })
      }
    }
  }

  const chooseTemplate = (item) => {
    setTemplate(item)
    setItems(item.items)
    setFilteredItems(item.items)
    setName(item.templateName)
    setFinalPrice(item.finalPrice)
  }

  async function save() {
    setIsLoading(true)
    setSaveError('')
    if (editTemplateMode) {
      if (!templateName) {
        setSaveError(t('notAllFieldsFilled'))
        setIsLoading(false)
        return
      }
      let info = {
        templateName,
        createdDate,
        items,
        finalPrice: parseFloat(finalPrice),
        finalExpensesPrice: parseFloat(finalPrice) - expectedProfit(true),
        dailyStatID: getDailyStatID(store, createdDate),
        organizationId: context.user.organizationId,
        store,
        lastEditedBy: context.user.uid,
      }
      if (detail) {
        try {
          await updateComplexSaleTemplate(detail.ref, info)
          setSuccessMessageVisible(true)
          setIsLoading(false)
        } catch (error) {
          Sentry.captureException(error)
          console.log(error)
          setSaveError(t('error'))
          setIsLoading(false)
        }
      } else {
        try {
          let ref = await saveNewComplexSaleTemplate(db, info)
          setSuccessMessageVisible(true)
          setIsLoading(false)
        } catch (error) {
          Sentry.captureException(error)
          console.log(error)
          setSaveError(t('error'))
          setIsLoading(false)
        }
      }
    } else {
      if (!name || !finalPrice) {
        setSaveError(t('notAllFieldsFilled'))
        setIsLoading(false)
        return
      }
      let info = {
        template,
        name,
        createdDate,
        items,
        finalPrice: parseFloat(finalPrice),
        finalExpensesPrice: parseFloat(finalPrice) - expectedProfit(true),
        comments,
        client,
        status: statusForWrite(status, localizedStatuses),
        isActive: complexSaleIsActive(status),
        createdBy: context.user.uid,
        dailyStatID: getDailyStatID(store, createdDate),
        store: store,
        organizationId: context.user.organizationId,
        updates,
        payments: payments
      }
      if (detail) {
        // No implementation
      } else {
        try {
          let ref = await saveNewComplexSale(db, info)
          addItemToData({...info, ref, id: ref.id}, info.createdDate, info.store)
          sendFBAnalyticsEvent('new_complex_sale')
          postHog.capture('complex_sale_created')
          setSuccessMessageVisible(true)
          setIsLoading(false)
        } catch (error) {
          Sentry.captureException(error, { tags: { books_write_error: 'complex-sale-create' } })
          console.log(error)
          if (error.message === 'INGREDIENT_NOT_FOUND') {
            setSaveError(t('ingredientNotFoundError', { 
              name: error.ingredientName,
              product: error.productName 
            }))
          } else {
            setSaveError(saveFailureText(error, t, t('error')))
          }
          setIsLoading(false)
        }
      }
    }
  }

  const addItem = (item) => {
    setItems((items) => {
      let newArr = items ? [...items] : []
      let itemIndex = newArr.findIndex(x => x.id === item.id)
      if (itemIndex > -1) {
        let oldItem = newArr[itemIndex]
        let newItem = {...oldItem, quantity: parseFloat(oldItem.quantity) + 1}
        newArr[itemIndex] = newItem
      } else {
        let newItem = {
          ...item,
          text: item.name,
          quantity: 1,
          salePrice: item.salePrices[store],
        }
        newArr.push(newItem)
      }
      setFilteredItems(newArr)
      return newArr
    })
  }
  
  const addEmptyRow = () => {
    setItems((items) => {
      let newArr = items ? [...items] : []
      let newItem = {
        id: 'custom-' + Date.now(),
        name: '',
        text: '',
        quantity: 1,
        purchasePrice: 0,
        salePrice: 0,
        type: 1,
        packagePrice: 0,
      }
      newArr.push(newItem)
      setFilteredItems(newArr)
      return newArr
    })
  }

  const setProductName = (index, value) => {
    if (filteredItems.length != items.length) {
      let newArr = [...filteredItems]
      let oldItem = newArr[index]
      let newItem = {...oldItem, name: value}
      newArr[index] = newItem
      setFilteredItems(newArr)
      let idx = items.findIndex((item) => item.id == oldItem.id)
      newArr = [...items]
      oldItem = newArr[idx]
      newItem = {...oldItem, name: value}
      newArr[idx] = newItem
      setItems(newArr)
    } else {
      let newArr = [...items]
      let oldItem = newArr[index]
      let newItem = {...oldItem, name: value}
      newArr[index] = newItem
      setItems(newArr)
      setFilteredItems(newArr)
    }
  }

  const setItemQuantity = (index, value) => {
    if (filteredItems.length != items.length) {
      let newArr = [...filteredItems]
      let oldItem = newArr[index]
      let newItem = {...oldItem, quantity: value}
      newArr[index] = newItem
      setFilteredItems(newArr)
      let idx = items.findIndex((item) => item.id == oldItem.id)
      newArr = [...items]
      oldItem = newArr[idx]
      newItem = {...oldItem, quantity: value}
      newArr[idx] = newItem
      setItems(newArr)
    } else {
      let newArr = [...items]
      let oldItem = newArr[index]
      let newItem = {...oldItem, quantity: value}
      newArr[index] = newItem
      setItems(newArr)
      setFilteredItems(newArr)
    }
  }

  const setPurchasePrice = (index, value) => {
    if (filteredItems.length != items.length) {
      let newArr = [...filteredItems]
      let oldItem = newArr[index]
      let newItem = {...oldItem, purchasePrice: value}
      newArr[index] = newItem
      setFilteredItems(newArr)
      let idx = items.findIndex((item) => item.id == oldItem.id)
      newArr = [...items]
      oldItem = newArr[idx]
      newItem = {...oldItem, purchasePrice: value}
      newArr[idx] = newItem
      setItems(newArr)
    } else {
      let newArr = [...items]
      let oldItem = newArr[index]
      let newItem = {...oldItem, purchasePrice: value}
      newArr[index] = newItem
      setItems(newArr)
      setFilteredItems(newArr)
    }
  }

  const setPackagePrice = (index, value) => {
    if (filteredItems.length != items.length) {
      let newArr = [...filteredItems]
      let oldItem = newArr[index]
      let newItem = {...oldItem, packagePrice: value}
      newArr[index] = newItem
      setFilteredItems(newArr)
      let idx = items.findIndex((item) => item.id == oldItem.id)
      newArr = [...items]
      oldItem = newArr[idx]
      newItem = {...oldItem, packagePrice: value}
      newArr[idx] = newItem
      setItems(newArr)
    } else {
      let newArr = [...items]
      let oldItem = newArr[index]
      let newItem = {...oldItem, packagePrice: value}
      newArr[index] = newItem
      setItems(newArr)
      setFilteredItems(newArr)
    }
  }

  const totalPurchaseSum = (returnNumber=false) => {
    var sum = 0
    items?.forEach(item => {
      sum += parseFloat(item.quantity) * (parseFloat(item.purchasePrice) + (parseFloat(item.packagePrice || 0)))
    })
    return returnNumber ? sum : currencyFormatter.format(sum)
  }

  const totalAdditionalExpenses = (returnNumber=false) => {
    var sum = 0
    return returnNumber ? sum : currencyFormatter.format(sum)
  }

  const expectedProfit = (returnNumber=false) => {
    var sum = 0
    sum = finalPrice - totalPurchaseSum(true) - totalAdditionalExpenses(true)
    return returnNumber ? sum : currencyFormatter.format(sum)
  }

  const saveStatus = async (newStatus) => {
    try {
      setStatus(newStatus)
      if (detail) {
        // Use centralized function for status update
        const { updatedData, operation } = await updateComplexSaleStatus(
          detail,
          newStatus,
          updates,
          context.user.uid
        )
        setUpdates([...updates, operation])
        editItemInData({...detail, ...updatedData}, sectionIndex, itemIndex)
      }
    } catch(error) {
      console.log(error)
      Sentry.captureException(error, { tags: { books_write_error: 'complex-sale-status-update' } })
    }
  }

  const TrashIcon = (style) => (
    <Icon {...style}  name='trash-outline'/>
  )
  const ArrowDownIcon = (style) => (
    <Icon {...style}  name='arrow-downward-outline'/>
  )
  const ArrowUpIcon = (style) => (
    <Icon {...style} name='arrow-upward-outline'/>
  )

  const deleteRow = (index) => {
    if (filteredItems.length != items.length) {
      let newArr = [...filteredItems]
      newArr.splice(index, 1)
      setFilteredItems(newArr)
      let oldItem = newArr[index]
      let idx = items.findIndex((item) => item.id == oldItem.id)
      newArr = [...items]
      newArr.splice(idx, 1)
      setItems(newArr)
    } else {
      let newArr = [...items]
      newArr.splice(index, 1)
      setItems(newArr)
      setFilteredItems(newArr)
    }
  }

  const moveRowDown = (index) => {
    if (filteredItems.length != items.length) {
      return
    } else {
      if (index+1 != items.length) {
        let newData = [...items]
        newData[index+1] = items[index]
        newData[index] = items[index+1]
        setItems(newData)
        setFilteredItems(newData)
      }
    }
  }

  const moveRowUp = (index) => {
    if (filteredItems.length != items.length) {
      return
    } else {
      if (index !== 0) {
        let newData = [...items]
        newData[index-1] = items[index]
        newData[index] = items[index-1]
        setItems(newData)
        setFilteredItems(newData)
      }
    }
  }

  const addComment = async () => {
    try {
      setCreateCommentModalVisible(false)
      if (commentText.length > 0) {
        let comment = {
          id: (detail?.id || 'new') + '-comment-' + Date.now(),
          text: commentText,
          date: new Date(),
          createdBy: context.user.uid,
          complexSaleId: detail?.id || 'new',
          store: store,
          organizationId: context.user.organizationId
        }
        if (detail) {
          // Use centralized function for adding comment
          const updatedData = await addComplexSaleComment(detail, comment, comments)
          editItemInData({...detail, ...updatedData}, sectionIndex, itemIndex)
        }
        setComments([...comments, comment])
        setCommentText('')
      }
    } catch(error) {
      console.log(error)
      Sentry.captureException(error, { tags: { books_write_error: 'complex-sale-comment-add' } })
    }
  }

  const saveFinalPrice = async () => {
    setIsLoading(true)
    try {
      if (detail) {
        let operation = {type: 'finalPriceUpdate', oldFinalPrice: detail.finalPrice, finalPrice: parseFloat(finalPrice), date: new Date(), updatedBy: context.user.uid}
        let updatedData = {
          finalPrice: parseFloat(finalPrice),
          updates: [...updates, operation]
        }
        await saveUpdatedFinalPriceForComplexSale(context.db, detail, updatedData, context.user.uid)
        setUpdates([...updates, operation])
        editItemInData({...detail, ...updatedData}, sectionIndex, itemIndex)
        toast.show(t('success'), 3000)
      }
    } catch(error) {
      Sentry.captureException(error, { tags: { books_write_error: 'complex-sale-final-price-update' } })
      console.log(error)
      toast.show(t('notSaved'), {duration: 3000, type: 'danger'})
      setFinalPrice(detail.finalPrice)
    }
    setEditFinalPriceMode(false)
    setIsLoading(false)
  }

  const saveItems = async () => {
    setIsLoading(true)
    try {
      if (detail) {
        let operation = {type: 'itemsUpdate', oldItems: detail.items, items: items, date: new Date(), updatedBy: context.user.uid}
        let updatedData = {
          items, 
          finalExpensesPrice: parseFloat(finalPrice) - expectedProfit(true),
          updates: [...updates, operation]
        }
        await saveUpdatedItemsForComplexSale(context.db, detail, updatedData)
        setUpdates([...updates, operation])
        editItemInData({...detail, ...updatedData}, sectionIndex, itemIndex)
        setEditMode(false)
        toast.show(t('success'), 3000)
      }
    } catch(error) {
      Sentry.captureException(error, { tags: { books_write_error: 'complex-sale-items-update' } })
      console.log(error)
      toast.show(t('notSaved'), {duration: 3000, type: 'danger'})
    }
    setIsLoading(false)
  }

  const addPayment = async () => {
    try {
      setCreatePaymentModalVisible(false)
      if (paymentAmount && parseFloat(paymentAmount) > 0 && paymentMethodForPayment) {
        let payment = {
          id: (detail?.id || 'new') + '-payment-' + Date.now(),
          amount: parseFloat(paymentAmount),
          description: paymentDescription || t('complexSales.payment'),
          paymentMethod: paymentMethodForPayment,
          date: new Date(),
          createdBy: context.user.uid,
          complexSaleId: detail?.id || 'new',
          store: store,
          organizationId: context.user.organizationId
        }

        // Create transaction for the payment
        if (detail) {
          const result = await addComplexSalePayment(
            context.db,
            detail,
            payment,
            payments,
            store,
            context.user.organizationId
          )

          // Update UI with payment that includes transactionId
          const updatedPayments = [...payments, result.payment]
          editItemInData({...detail, payments: updatedPayments}, sectionIndex, itemIndex)
          setPayments(updatedPayments)
        } else {
          setPayments([...payments, payment])
        }
        setPaymentAmount('')
        setPaymentDescription('')
        setPaymentMethodForPayment(null)
      }
    } catch(error) {
      Sentry.captureException(error, { tags: { books_write_error: 'complex-sale-payment-add' } })
      console.log(error)
      // ATL-2004: the payment may already be saved; say so rather than invite a second payment.
      if (isWriteOutcomeUnknown(error)) toast.show(t('writeOutcomeUnknown.record'), { duration: 8000, type: 'danger' })
    }
  }

  const deletePayment = async (payment) => {
    try {
      if (detail) {
        const updatedPayments = await deleteComplexSalePayment(
          context.db,
          detail,
          payment,
          payments,
          store
        )

        // Update UI
        editItemInData({...detail, payments: updatedPayments}, sectionIndex, itemIndex)
        setPayments(updatedPayments)
      } else {
        const updatedPayments = payments.filter(p => p.id !== payment.id)
        setPayments(updatedPayments)
      }
    } catch(error) {
      Sentry.captureException(error, { tags: { books_write_error: 'complex-sale-payment-delete' } })
      console.log(error)
    }
  }

  const getTotalPaidAmount = () => {
    return payments.reduce((sum, payment) => sum + payment.amount, 0)
  }

  const getRemainingAmount = () => {
    return parseFloat(finalPrice) - getTotalPaidAmount()
  }

  const deleteComment = async (comment) => {
    try {
      if (detail) {
        // Use centralized function for deleting comment
        const updatedComments = await deleteComplexSaleComment(detail, comment.id, comments)
        editItemInData({...detail, comments: updatedComments}, sectionIndex, itemIndex)
        setComments(updatedComments)
      } else {
        const updatedComments = comments.filter(c => c.id !== comment.id)
        setComments(updatedComments)
      }
    } catch(error) {
      Sentry.captureException(error, { tags: { books_write_error: 'complex-sale-comment-delete' } })
    }
  }

  return (
    template == null && !editTemplateMode ? (
      <ComplexSaleTemplatesList onItemPress={(item, index)=>{chooseTemplate(item)}} showEmptyTemplate={true} />
    ) : (
      <UniversalDetailScreen 
        navigation={navigation}
        title={editTemplateMode ? (detail ? t('complexSales.editTemplate') : t('complexSales.newTemplate')) : (detail ? t('complexSales.editComplexSale') : t('complexSales.newComplexSale'))}
        onDelete={detail != null && (userRole?.permissions?.deleteComplexSale || userRole?.id === 'admin') ? handleDelete : null}
      >
        {detail && <Text appearance='hint' style={{textAlign: 'center', marginVertical: 10}}>
          {detail?.id}
        </Text>}
        <Text category='label' style={styles.label}>
          {t('complexSales.template')}
        </Text>
        { editTemplateMode ? (
          <StyledTextInput
            value={templateName}
            onChangeText={setTemplateName}
            style={styles.field}
            returnKeyType='done'
            placeholder=''
            autoCapitalize='sentences'
          />
        ) : (
          /* ATL-1024: `template` is a NESTED object in Firestore (`template.templateName`, 994/994
             documents) but the API flattens it to `templateName` — `complex_sales` has no
             `template` column at all, only `template_name`, so there is no snapshot to return.
             Dereferencing the nested shape threw `undefined is not an object` and took the whole
             detail screen down for any cut-over org. Read whichever shape arrived; do NOT
             reconstruct a partial `template` object, because this state is written back verbatim
             when a new sale is saved (line ~211). */
          <Text>{template?.templateName ?? templateName ?? ''}</Text>
        )}
        { detail && !editTemplateMode && (
          <>
            <Text category='label' style={styles.label}>
              {t('complexSales.createdDate')}
            </Text>
            <Text>{format(createdDate, 'd MMM HH:mm')}</Text>
          </>
        )}
        { !editTemplateMode && (
          <>
            { Object.values(storesDict).length > 1 && (userRole?.permissions?.['viewOtherStores'] || context.user.role === 'admin') && (
              <>
                <Text category='label' style={styles.label}>
                  {t('store')}
                </Text>
                <PlatformDependantSelect
                  data={Object.values(storesDict)}
                  selectedOption={storesDict?.[store]}
                  onSelect={(itemValue, itemIndex) => {
                    setStore(itemValue.id)
                  }}
                  disabled={detail != null}
                />
              </>
            )}
            { client ? (
              <>
                <Text category='label' style={styles.label}>{t('complexSales.client')}</Text>
                <View style={{flexDirection: 'row', alignItems: 'center'}}>
                  {/* ATL-1024: Firestore's client ref spells the name `displayName` (97/97 documents);
                      the API transform emitted only `name`, so a cut-over org rendered
                      "+7700, undefined" here. The backend now sends both — this fallback keeps the
                      row readable whichever shape reaches it, and costs nothing. */}
                  <Text style={{marginRight: 5}}>{client.phoneNumber + ', ' + (client.displayName ?? client.name ?? '')}</Text>
                  { !detail && <Button style={styles.iconButton} appearance='ghost' status='danger' accessoryLeft={TrashIcon} onPress={() => {setClient(null)}}/>}
                </View>
              </>
            ) : (
              <>
                <View style={[{flexDirection: 'row', alignItems: 'center'}, styles.label]}>
                  <Text category='label' style={{marginRight: 5}}>{t('complexSales.client')}</Text>
                  <TouchableOpacity onPress={() => setCreateClientModalVisible(true)}>
                    <Icon name='plus-circle-outline' height={16} width={16} fill={colors.primary}/>
                  </TouchableOpacity>
                </View>
                <Modal style={[styles.modal, {padding: 30, backgroundColor: theme['background-basic-color-1']}]}
                  visible={createClientModalVisible} 
                  backdropStyle={styles.backdrop} 
                  onBackdropPress={() => setCreateClientModalVisible(false)}
                >
                  <TouchableOpacity 
                    style={{position: 'absolute', top: 10, right: 10, alignSelf: 'flex-end', alignItems: 'center', justifyContent: 'center'}} 
                    onPress={() => {
                      setCreateClientModalVisible(false)
                    }}
                  > 
                    <Icon name='close-circle' width={32} height={32} fill={theme['text-basic-color']} />
                  </TouchableOpacity>
                  <CreateClientView db={context.db} defaultBonusLevel={constants?.loyaltySettings?.defaultBonusLevel} 
                    store={store} storeInfo={storesDict?.[store]} organizationId={context.user.organizationId} onCreate={(client) => {
                      setClient(client)
                      setCreateClientModalVisible(false)
                    }}
                  />
                </Modal>
                <ClientAutoComplete db={context.db} organizationId={context.user.organizationId} onSelect={setClient} disabled={detail != null} />
              </>
            )}
            <Text category='label' style={styles.label}>
              {t('complexSales.name')}
            </Text>
            <StyledTextInput
              value={name}
              onChangeText={setName}
              style={styles.field}
              returnKeyType='done'
              placeholder=''
              autoCapitalize='sentences'
              disabled={detail != null}
            />
            <Text category='label' style={styles.label}>
              {t('complexSales.status')}
            </Text>
            <PlatformDependantSelect
              data={localizedStatuses}
              selectedOption={status}
              onSelect={saveStatus}
              style={styles.spacedItem}
            />
          </>
        )}
        <View style={[{flexDirection: 'row', alignItems: 'center'}, styles.label]}>
          <Text category='label' style={{marginRight: 5}}>{t('complexSales.itemsList')}</Text>
          { detail && !editTemplateMode && !editMode && (
            <TouchableOpacity onPress={() => setEditMode(true)}>
              <Icon name='edit-outline' height={16} width={16} fill={colors.primary}/>
            </TouchableOpacity>
          )}
        </View>
        {editMode && (
          <View style={{ marginBottom: 10}}>
            <ProductSearch style={{marginRight: 10}} onSelectCallback={addItem} placeholder='complexSales.addProduct' store={store} />
            <Button size='small' appearance='ghost' onPress={addEmptyRow}>{t('complexSales.addEmptyRow')}</Button>
          </View>
        )}
        <ScrollView horizontal={true} style={[styles.card, {backgroundColor: theme['background-basic-color-1']}]}>
          <View>
            <View style={styles.fieldContainer} >
              { editMode && (<View style={{width: 68}}></View>)}
              <Text category='label' style={styles.fieldLabel}>
                {t('complexSales.name')}
              </Text>
              <Text category='label' style={styles.quantityFieldName}>
                {t('complexSales.quantity')}
              </Text>
              <Text category='label' style={styles.priceField}>
                {t('complexSales.purchasePrice')}
              </Text>
              <Text category='label' style={styles.priceField}>
                {t('complexSales.packagePrice')}
              </Text>
            </View>
            <Divider />
            {filteredItems && filteredItems.map((item, index) => (
              <View key={item.id}>
              <View style={styles.fieldContainer} >
                { editMode && (
                  <>
                    <Button style={styles.leftArrowButton} appearance='ghost' status='info'  accessoryLeft={ArrowDownIcon} onPress={() => {moveRowDown(index)}}/>
                    <Button style={styles.iconButton} appearance='ghost' status='info' accessoryLeft={ArrowUpIcon} onPress={() => {moveRowUp(index)}}/>
                  </>
                )}
                {editMode ? (
                  <StyledTextInput 
                    value={item.name}
                    onChangeText={text => setProductName(index, text)}
                    style={styles.fieldLabel}
                    autoCapitalize='sentences'
                    returnKeyType='done'
                    selectTextOnFocus={true}
                    disabled={!item.id?.startsWith('custom')}
                  />
                ) : (
                  <Text category='label' style={styles.fieldLabel}>
                    {item.name}
                  </Text>
                )}
                {editMode ? (
                  <>
                    <TouchableOpacity onPress={() => {
                      setItemQuantity(index, item.quantity-1)
                    }} style={styles.changeButton}>
                      <Icon name='minus-circle-outline' fill={theme['text-hint-color']} height={30} width={30}  />
                    </TouchableOpacity>
                    <StyledTextInput 
                      value={item.quantity?.toString()}
                      onChangeText={text => setItemQuantity(index, text.replace(/,/g, '.'))}
                      keyboardType='decimal-pad'
                      style={styles.quantityField}
                      returnKeyType='done'
                      selectTextOnFocus={true}
                      controlledInput={true}
                    />
                    <TouchableOpacity onPress={() => {
                      setItemQuantity(index, item.quantity+1)
                    }} style={styles.changeButton}>
                      <Icon name='plus-circle-outline' fill={theme['text-hint-color']} height={30} width={30} />
                    </TouchableOpacity>
                  </>
                ) : (
                  <Text style={styles.quantityFieldName}>
                    {item.quantity + ' ' + (productUnitsDict[item.productUnit]?.shortName || t('pcs'))}
                  </Text>
                )}
                {editMode ? (
                  <StyledTextInput 
                    value={item.purchasePrice?.toString()}
                    onChangeText={text => setPurchasePrice(index, text.replace(/,/g, '.'))}
                    keyboardType='decimal-pad'
                    style={styles.priceField}
                    returnKeyType='done'
                    selectTextOnFocus={true}
                  />
                ) : (
                  <Text style={styles.priceField}>
                    {currencyFormatter.format(item.purchasePrice)}
                  </Text>
                )}
                {editMode ? (
                  <StyledTextInput 
                    value={(item.packagePrice || 0)?.toString()}
                    onChangeText={text => setPackagePrice(index, text.replace(/,/g, '.'))}
                    keyboardType='decimal-pad'
                    style={styles.priceField}
                    returnKeyType='done'
                    selectTextOnFocus={true}
                  />
                ) : (
                  <Text style={styles.priceField}>
                    {currencyFormatter.format(item.packagePrice || 0)}
                  </Text>
                )}
                {editMode && (
                  <Button style={styles.iconButton} appearance='ghost' status='danger' accessoryLeft={TrashIcon} onPress={() => {deleteRow(index)}}/>
                )}
              </View>
              <Divider />
              </View>
            ))}
            {(!items || items.length === 0) && (
              <Text style={{marginVertical: 10}} appearance='hint'>{t('complexSales.emptyItems')}</Text>
            )}
            <Text category='label' style={styles.label}>
              {t('complexSales.totalPurchaseSum') + ': ' + totalPurchaseSum()}
            </Text>
            { detail && editMode && !editTemplateMode && (
              <>
                {isLoading ? (
                  <View style={styles.spinner}><Spinner /></View>
                ) : (
                  !successMessageVisible && (
                  <Button onPress={saveItems} style={styles.submitButton}>
                    {t('save')}
                  </Button>
                  )
                )}
              </>
            )}
          </View>
        </ScrollView>
        <View style={[{flexDirection: 'row', alignItems: 'center'}, styles.label]}>
          <Text category='label' style={{marginRight: 5}}>{t('complexSales.finalPrice')}</Text>
          { detail && !editTemplateMode && !editFinalPriceMode && (
            <TouchableOpacity onPress={() => setEditFinalPriceMode(true)}>
              <Icon name='edit-outline' height={16} width={16} fill={colors.primary}/>
            </TouchableOpacity>
          )}
        </View>
        <View style={[styles.card, {backgroundColor: theme['background-basic-color-1']}]}>
          <StyledTextInput 
            value={finalPrice}
            onChangeText={setFinalPrice}
            keyboardType='decimal-pad'
            style={{marginBottom: 10}}
            returnKeyType='done'
            selectTextOnFocus={true}
            controlledInput={false}
            disabled={detail != null && !editTemplateMode && !editFinalPriceMode}
          />
          <Text style={styles.totalSum}>
            {t('complexSales.expectedProfit') + ': ' + expectedProfit()}
          </Text>
          { detail && !editTemplateMode && editFinalPriceMode && (
            <>
              {isLoading ? (
                <View style={styles.spinner}><Spinner /></View>
              ) : (
                !successMessageVisible && (
                <Button onPress={saveFinalPrice} style={styles.submitButton}>
                  {t('save')}
                </Button>
                )
              )}
            </>
          )}
        </View>
        { !editTemplateMode && (
          <>            
            <View style={[{flexDirection: 'row', alignItems: 'center'}, styles.label]}>
              <Text category='label' style={{marginRight: 5}}>{t('complexSales.payments')}</Text>
              <TouchableOpacity onPress={() => setCreatePaymentModalVisible(true)}>
                <Icon name='plus-circle-outline' height={16} width={16} fill={colors.primary}/>
              </TouchableOpacity>
            </View>
            <AdaptiveTableCard
              data={payments}
              fields={[
                {
                  key: 'amount',
                  title: t('complexSales.amount'),
                  type: 'text',
                  formatter: (value) => currencyFormatter.format(value),
                  columnStyle: 'nameColumn'
                },
                {
                  key: 'date',
                  title: t('date'),
                  type: 'text',
                  getValue: (payment) => payment?.date?.toDate?.() ? format(payment?.date?.toDate(), 'd MMM HH:mm') : t('now'),
                },
                {
                  key: 'description',
                  title: t('complexSales.description'),
                  type: 'text'
                },
                {
                  key: 'paymentMethod',
                  title: t('complexSales.paymentMethod'),
                  type: 'text',
                  formatter: (value) => {
                    return (
                      <View style={{flexDirection: 'row', alignItems: 'center'}}>
                        <FontAwesome5 style={{marginRight: 4}} name={value?.icon} size={10} color={theme['text-hint-color']} />
                        <Text category='c1'>{value?.iconText || '-'}</Text>
                      </View>
                    )
                  }
                },
                {
                  key: 'createdBy',
                  title: t('employee'),
                  type: 'text',
                  getValue: (payment) => usersDict[payment.createdBy]?.name || t('complexSales.deletedAuthor')
                },
                {
                  key: 'delete',
                  type: 'iconButton',
                  iconName: 'trash-outline',
                  iconColor: colors.danger,
                  onPress: deletePayment
                }
              ]}
              renderFooter={() => (
                <View style={{marginTop: 10}}>
                  <Text style={styles.totalSum}>
                    {t('complexSales.totalPaid') + ': ' + currencyFormatter.format(getTotalPaidAmount())}
                  </Text>
                  <Text style={styles.totalSum}>
                    {t('complexSales.remainingAmount') + ': ' + currencyFormatter.format(getRemainingAmount())}
                  </Text>
                </View>
              )}
            />
            <Modal style={[styles.modal, {backgroundColor: theme['background-basic-color-1']}]}
              visible={createPaymentModalVisible}
              backdropStyle={styles.backdrop} 
              onBackdropPress={() => setCreatePaymentModalVisible(false)}
            >
              <TouchableOpacity 
                style={{position: 'absolute', top: 10, right: 10, zIndex: 20, alignSelf: 'flex-end', alignItems: 'center', justifyContent: 'center'}} 
                onPress={() => {
                  setCreatePaymentModalVisible(false)
                }}
              > 
                <Icon name='close-circle' width={32} height={32} fill={theme['text-basic-color']} />
              </TouchableOpacity>
              <ScrollView style={{marginVertical: 10, padding: 30}}>
                <Text category='h5' style={{marginBottom: 10}}>{t('complexSales.newPayment')}</Text>
                <Text category='label' style={styles.label}>
                  {t('complexSales.amount')}
                </Text>
                <StyledTextInput 
                  value={paymentAmount}
                  onChangeText={setPaymentAmount}
                  keyboardType='decimal-pad'
                  returnKeyType='done'
                  selectTextOnFocus={true}
                />
                <Text category='label' style={styles.label}>
                  {t('complexSales.description')}
                </Text>
                <StyledTextInput 
                  value={paymentDescription}
                  onChangeText={setPaymentDescription}
                  autoCapitalize='sentences'
                  returnKeyType='done'
                  selectTextOnFocus={true}
                />
                <Text category='label' style={styles.label}>
                  {t('complexSales.paymentMethod')}
                </Text>
                <PaymentMethodsSelect 
                  paymentMethod={paymentMethodForPayment} 
                  setPaymentMethod={setPaymentMethodForPayment} 
                  paymentMethods={constants?.paymentMethods?.data} 
                  style={{marginBottom: 5}} 
                  store={store} 
                />
                <Button style={{marginTop: 10}} onPress={addPayment} disabled={!paymentMethodForPayment}>
                  {t('complexSales.add')}
                </Button>
              </ScrollView>
            </Modal>
          </>
        )}
        { !editTemplateMode && (
          <>
            <View style={[{flexDirection: 'row', alignItems: 'center'}, styles.label]}>
              <Text category='label' style={{marginRight: 5}}>{t('complexSales.comments')}</Text>
              <TouchableOpacity onPress={() => setCreateCommentModalVisible(true)}>
                <Icon name='plus-circle-outline' height={16} width={16} fill={colors.primary}/>
              </TouchableOpacity>
            </View>
            <AdaptiveTableCard
              data={comments}
              fields={[
                {
                  key: 'text',
                  title: t('complexSales.comment'),
                  type: 'text',
                  columnStyle: 'nameColumn'
                },
                {
                  key: 'dateAndAuthor',
                  title: t('complexSales.dateAndAuthor'),
                  type: 'text',
                  getValue: (comment) => {
                    const dateStr = comment?.date?.toDate?.() ? format(comment?.date?.toDate(), 'd MMM HH:mm') : t('now')
                    const author = usersDict[comment.user]?.name || usersDict[comment.createdBy]?.name || t('complexSales.deletedAuthor')
                    return `${dateStr}, ${author}`
                  },
                },
                {
                  key: 'delete',
                  type: 'iconButton',
                  iconName: 'trash-outline',
                  iconColor: colors.danger,
                  onPress: deleteComment
                }
              ]}
            />

            <Modal style={[styles.modal, {backgroundColor: theme['background-basic-color-1']}]}
              visible={createCommentModalVisible}
              backdropStyle={styles.backdrop} 
              onBackdropPress={() => setCreateCommentModalVisible(false)}
            >
              <TouchableOpacity 
                style={{position: 'absolute', top: 10, right: 10, zIndex: 20, alignSelf: 'flex-end', alignItems: 'center', justifyContent: 'center'}} 
                onPress={() => {
                  setCreateCommentModalVisible(false)
                }}
              > 
                <Icon name='close-circle' width={32} height={32} fill={theme['text-basic-color']} />
              </TouchableOpacity>
              <ScrollView style={{marginVertical: 10, padding: 30}}>
                <Text category='h5' style={{marginBottom: 10}}>{t('complexSales.newComment')}</Text>
                <Text category='label' style={styles.label}>
                  {t('complexSales.commentText')}
                </Text>
                <StyledTextInput 
                  value={commentText}
                  onChangeText={setCommentText}
                  autoCapitalize='sentences'
                  returnKeyType='done'
                  selectTextOnFocus={true}
                />
                <Button style={{marginTop: 10}} onPress={addComment}>{t('complexSales.add')}</Button>
              </ScrollView>
            </Modal>
          </>
        )}
        {saveError != '' && (
          <Text category='p1' status='danger' style={styles.label}>
            {saveError}
          </Text>
        )}
        { successMessageVisible && (
          <>
          <Text status='success'>{t('success')}</Text>
          <Button onPress={() => navigation.goBack()} style={styles.submitButton}>
            {t('back')}
          </Button>
          </>
        )}
        { (!detail || editTemplateMode) && (
          <>
            {isLoading ? (
              <View style={styles.spinner}><Spinner /></View>
            ) : (
              !successMessageVisible && (
              <Button onPress={save} style={styles.submitButton}>
                {t('save')}
              </Button>
              )
            )}
          </>
        )}
      </UniversalDetailScreen>
    )
  )
}

const styles = StyleSheet.create({
  label: {
    marginBottom: 8,
    marginTop: 16,
  },
  submitButton: {
    marginTop: 16,
  },
  spinner: {
    marginTop: 15,
    alignItems: 'center'
  },
  fieldContainer: {
    marginVertical: 4,
    flexDirection: 'row',
    alignItems: 'center',
  },
  fieldLabel: {
    width: 200,
    marginRight: 4,
  }, 
  priceField: {
    marginLeft: 8,
    marginRight: 4,
    width: 120,
    alignItems: 'flex-start',
  },
  quantityField: {
    width: 80,
  },
  quantityFieldName: {
    width: 80,
    marginHorizontal: 36,
  },
  changeButton: {
    marginHorizontal: 3,
  },
  totalSum: {
    marginBottom: 5,
  },
  iconButton: {
    width: 15,
  },
  leftArrowButton: {
    width: 15,
    marginLeft: -12
  },
  modal: {
    top: 100,
    width: '80%',
    height: '80%',
    backgroundColor: 'white',
    borderRadius: 20,
  },
  backdrop: {
    backgroundColor: 'rgba(0, 0, 0, 0.5)',
  },
  card: {
    backgroundColor: '#FFFFFF', 
    padding: 16, 
    borderRadius: 6
  },
});